/**
 * Phase 18.16-A — Online betalen PageBanner admin editor.
 *
 * Single-section editor: lets owner/admin/content-manager tweak the
 * banner copy + USP badges that surface on /online-betalen.
 *
 * Backend:
 *   GET /api/admin/online-payment-settings
 *   PUT /api/admin/online-payment-settings/page_banner
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";

const BADGE_ICON_OPTIONS = [
  "shield", "zap", "receipt", "check", "clock",
  "banknote", "support", "sparkles", "info", "package",
];

const FALLBACK = {
  eyebrow: "",
  title: "",
  highlight: "",
  subtitle: "",
  badges: [],
};

export default function OnlinePaymentSubSection() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [banner, setBanner] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/online-payment-settings");
      const pb = r?.data?.sections?.page_banner;
      if (pb && typeof pb === "object") {
        setBanner({
          ...FALLBACK,
          ...pb,
          badges: Array.isArray(pb.badges) ? pb.badges : [],
        });
      }
    } catch (e) {
      toast.error(formatApiError(e, "Kon instellingen niet laden"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setBanner((b) => ({ ...b, [k]: v }));
  const setBadgeField = (i, k, v) =>
    setBanner((b) => {
      const list = [...(b.badges || [])];
      list[i] = { ...(list[i] || {}), [k]: v };
      return { ...b, badges: list };
    });
  const addBadge = () =>
    setBanner((b) => ({ ...b, badges: [...(b.badges || []), { icon: "shield", label: "" }] }));
  const removeBadge = (i) =>
    setBanner((b) => {
      const list = [...(b.badges || [])];
      list.splice(i, 1);
      return { ...b, badges: list };
    });

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/online-payment-settings/page_banner", {
        payload: banner,
      });
      const pb = r?.data?.sections?.page_banner;
      if (pb) setBanner({ ...FALLBACK, ...pb, badges: Array.isArray(pb.badges) ? pb.badges : [] });
      toast.success("Online betalen banner opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm" data-testid="online-payment-sub-section-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Instellingen laden...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="online-payment-sub-section">
      {!canEdit && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-chivo font-bold text-lg text-[#082c50]">PageBanner</h3>
          <Button
            onClick={save}
            disabled={!canEdit || saving}
            className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
            data-testid="online-payment-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</> : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
          </Button>
        </div>

        <div>
          <Label>Eyebrow (chip-tekst)</Label>
          <Input
            value={banner.eyebrow ?? ""}
            onChange={(e) => setField("eyebrow", e.target.value)}
            disabled={!canEdit}
            data-testid="online-payment-eyebrow-input"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Hoofdtitel</Label>
            <Input
              value={banner.title ?? ""}
              onChange={(e) => setField("title", e.target.value)}
              disabled={!canEdit}
              data-testid="online-payment-title-input"
            />
          </div>
          <div>
            <Label>Highlight (gradient stuk)</Label>
            <Input
              value={banner.highlight ?? ""}
              onChange={(e) => setField("highlight", e.target.value)}
              disabled={!canEdit}
              data-testid="online-payment-highlight-input"
            />
          </div>
        </div>
        <div>
          <Label>Subtitle / beschrijving</Label>
          <Textarea
            rows={3}
            value={banner.subtitle ?? ""}
            onChange={(e) => setField("subtitle", e.target.value)}
            disabled={!canEdit}
            data-testid="online-payment-subtitle-input"
          />
        </div>

        <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-[#082c50]">USP-badges (max 5)</span>
            {canEdit && (banner.badges?.length || 0) < 5 && (
              <Button size="sm" variant="outline" onClick={addBadge} className="rounded-lg text-xs" data-testid="online-payment-badge-add">
                <Plus className="w-3 h-3 mr-1" /> Toevoegen
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {(banner.badges || []).map((b, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-slate-200" data-testid={`online-payment-badge-row-${i}`}>
                <div className="min-w-[120px]">
                  <Label className="text-[10px] uppercase tracking-wide">Icon</Label>
                  <select
                    value={b.icon || "shield"}
                    onChange={(e) => setBadgeField(i, "icon", e.target.value)}
                    disabled={!canEdit}
                    className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
                  >
                    {BADGE_ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-[10px] uppercase tracking-wide">Label</Label>
                  <Input
                    value={b.label || ""}
                    onChange={(e) => setBadgeField(i, "label", e.target.value)}
                    disabled={!canEdit}
                    data-testid={`online-payment-badge-label-${i}`}
                  />
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => removeBadge(i)} className="rounded-lg text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
            {(banner.badges || []).length === 0 && (
              <div className="text-xs text-gray-500 italic py-2">Nog geen badges.</div>
            )}
          </div>
        </div>

        <div className="text-[11px] text-gray-500 leading-relaxed">
          Tip: wijzigingen verschijnen direct op <strong>/online-betalen</strong>. De Stripe Buy Button en
          finance-instructies blijven ongewijzigd onder de banner.
        </div>
      </div>
    </div>
  );
}
